import { useState, useEffect } from "react";
import axios from "axios";
import emailjs from "@emailjs/browser";
import { toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;

const PONYVA_TYPES = [
  "Járműponyva-készítés",
  "Járműponyva-javítás",
  "Reklámdekoráció",
  "Sátor / mobilraktár",
  "Előtető / árnyékoló",
  "Használt ponyva vásárlás",
  "Medenceponyva",
  "Egyéb / egyedi",
];

export const QuoteForm = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    ponyva_type: "",
    description: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (EMAILJS_PUBLIC_KEY) {
      try {
        emailjs.init({ publicKey: EMAILJS_PUBLIC_KEY });
      } catch (e) {
        console.warn("EmailJS init failed:", e);
      }
    }
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.ponyva_type) {
      toast.error("Kérjük, töltse ki a kötelező mezőket (név, telefon, ponyva típus).");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        ponyva_type: form.ponyva_type,
        description: form.description.trim(),
      };
      if (form.email.trim()) payload.email = form.email.trim();

      // Save to backend (MongoDB)
      await axios.post(`${API}/quote`, payload);

      // Send email notification via EmailJS
      let emailSent = false;
      try {
        const emailResult = await emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_TEMPLATE_ID,
          {
            name: payload.name,
            phone: payload.phone,
            email: payload.email || "-",
            ponyva_type: payload.ponyva_type,
            description: payload.description || "-",
            created_at: new Date().toLocaleString("hu-HU"),
            to_email: "info@fortunaponyva.hu",
            reply_to: payload.email || "noreply@fortunaponyva.hu",
          }
        );
        console.log("EmailJS sent:", emailResult);
        emailSent = true;
      } catch (emailErr) {
        console.error("EmailJS error:", emailErr?.status, emailErr?.text, emailErr);
      }

      setSuccess(true);
      toast.success(
        emailSent
          ? "Köszönjük! Hamarosan visszajelzünk."
          : "Köszönjük! Rögzítettük az ajánlatkérést."
      );
      setForm({
        name: "",
        phone: "",
        email: "",
        ponyva_type: "",
        description: "",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Hiba történt a küldés során. Kérjük, próbálja újra.";
      toast.error(typeof msg === "string" ? msg : "Hiba történt.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="quote-form-wrapper"
      className="bg-white p-6 md:p-10 rounded-lg shadow-xl border border-gray-100 text-gray-900"
    >
      {success ? (
        <div
          data-testid="quote-success"
          className="text-center py-8 flex flex-col items-center"
        >
          <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <h3 className="mt-5 font-heading font-bold text-[#0B192C] text-2xl">
            Megkaptuk az ajánlatkérését!
          </h3>
          <p className="mt-3 text-gray-600 max-w-sm">
            Munkatársunk hamarosan felveszi Önnel a kapcsolatot a megadott
            elérhetőségen.
          </p>
          <Button
            data-testid="quote-new-btn"
            onClick={() => setSuccess(false)}
            className="mt-6 bg-[#1E3E62] hover:bg-[#152d49] text-white"
          >
            Új ajánlatkérés küldése
          </Button>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-5" data-testid="quote-form">
          <div>
            <span className="eyebrow">Ajánlatkérés</span>
            <h3 className="mt-2 font-heading font-bold text-[#0B192C] text-2xl md:text-3xl">
              Töltse ki az űrlapot, hamarosan jelentkezünk.
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              A *-gal jelölt mezők kitöltése kötelező.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="q-name" className="text-gray-700 font-medium">
                Név *
              </Label>
              <Input
                id="q-name"
                data-testid="quote-name-input"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Teljes név"
                className="mt-1.5 h-11 text-gray-900 caret-[#EA580C]"
                required
              />
            </div>
            <div>
              <Label htmlFor="q-phone" className="text-gray-700 font-medium">
                Telefonszám *
              </Label>
              <Input
                id="q-phone"
                data-testid="quote-phone-input"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="+36 30 123 4567"
                className="mt-1.5 h-11 text-gray-900 caret-[#EA580C]"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="q-email" className="text-gray-700 font-medium">
                E-mail
              </Label>
              <Input
                id="q-email"
                data-testid="quote-email-input"
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder="pelda@email.hu"
                className="mt-1.5 h-11 text-gray-900 caret-[#EA580C]"
              />
            </div>
            <div>
              <Label className="text-gray-700 font-medium">
                Milyen ponyvára van szüksége? *
              </Label>
              <Select
                value={form.ponyva_type}
                onValueChange={(v) => update("ponyva_type", v)}
              >
                <SelectTrigger
                  data-testid="quote-type-trigger"
                  className="mt-1.5 h-11 text-gray-900"
                >
                  <SelectValue placeholder="Válasszon típust" />
                </SelectTrigger>
                <SelectContent>
                  {PONYVA_TYPES.map((t) => (
                    <SelectItem
                      key={t}
                      value={t}
                      data-testid={`quote-type-${t.replace(/\s+/g, "-")}`}
                    >
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="q-desc" className="text-gray-700 font-medium">
              Méret vagy rövid leírás
            </Label>
            <Textarea
              id="q-desc"
              data-testid="quote-description-input"
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              placeholder="Pl. 6x2,5 m teherautó ponyva, kék színben, 2 oldali fülekkel..."
              className="mt-1.5 min-h-[120px] text-gray-900 caret-[#EA580C]"
            />
          </div>

          <Button
            data-testid="quote-submit-btn"
            type="submit"
            disabled={submitting}
            className="w-full h-12 bg-[#EA580C] hover:bg-[#C2410C] text-white font-semibold text-base shadow-md disabled:opacity-70"
          >
            {submitting ? (
              "Küldés..."
            ) : (
              <>
                Ajánlatkérés elküldése
                <Send className="w-4 h-4" />
              </>
            )}
          </Button>
          <p className="text-xs text-gray-500 text-center">
            Az adatait kizárólag a kapcsolatfelvételhez használjuk fel.
          </p>
        </form>
      )}
    </div>
  );
};

export default QuoteForm;
